import { useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  Badge,
  Box,
  Flex,
  Heading,
  LinkBox,
  LinkOverlay,
  ListItem,
  Progress,
} from "@chakra-ui/react";
import { rgba } from "polished";
import { gql } from "__generated__/gql";
import MarkdownText from "components/Shared/MarkdownText";
import { downloadFileUrl, getFileExtension } from "utils/files/utils";
import colors from "theme/foundations/colors";

const signedUrlQuery = gql(/* GraphQL */ `
  query ExampleDocumentSignedUrl(
    $documentId: UUID!
    $attachmentFilename: String
  ) {
    exampleDocument(id: $documentId) {
      id
      fileUrl(attachmentFilename: $attachmentFilename)
    }
  }
`);

type Props = {
  documentId: string;
  name: string;
  onClick?: (document: { id: UUID; name: string }) => void;
  originalFilename: string;
  description: string;
};

export default function ExampleDocument({
  description,
  documentId,
  name,
  onClick,
  originalFilename,
}: Props) {
  const ext = getFileExtension(originalFilename);
  const displayName =
    name || originalFilename.slice(0, ext ? (ext.length + 1) * -1 : undefined);
  const downloadName = displayName && ext ? `${displayName}.${ext}` : undefined;

  const [getSignedUrl, { loading }] = useLazyQuery(signedUrlQuery, {
    fetchPolicy: "no-cache",
    variables: { documentId, attachmentFilename: downloadName },
    onCompleted: (data) => {
      const { fileUrl } = data?.exampleDocument || {};
      if (!fileUrl) return;
      downloadFileUrl(fileUrl, downloadName);
    },
  });

  const handleClick = useCallback(() => {
    if (!documentId) return;
    getSignedUrl().catch(() => {});
    onClick?.({ id: documentId, name });
  }, [documentId, getSignedUrl, onClick, name]);

  return (
    <LinkBox
      as={ListItem}
      margin=".75rem 0"
      backgroundColor={rgba(colors.lightSoftBlue, 0.1)}
      borderColor="softBlue"
      borderWidth={1}
      borderRadius={10}
      padding="3"
      overflow="hidden"
    >
      <Flex>
        <Heading size="xs" color="magenta.dark">
          <LinkOverlay as="button" onClick={handleClick} disabled={loading}>
            {displayName}
          </LinkOverlay>
        </Heading>
        {ext && (
          <Badge
            fontSize=".65em"
            marginLeft="2"
            marginTop=".4em"
            alignSelf="center"
            color="softBlue"
            backgroundColor={rgba(colors.lightSoftBlue, 0.1)}
          >
            .{ext}
          </Badge>
        )}
      </Flex>

      {description && (
        <Box fontSize=".8em" marginTop=".25rem" marginBottom="-.25rem">
          <MarkdownText markdown={description} />
        </Box>
      )}

      {loading && (
        <Progress
          isIndeterminate
          size="xs"
          colorScheme="brand"
          position="absolute"
          top={0}
          left={0}
          right={0}
        />
      )}
    </LinkBox>
  );
}
